import type {
  NotificationPreferenceResponseDto,
  NotificationResponseDto,
  PushSubscriptionResponseDto,
  UnreadCountResponseDto,
} from '@trellis/api'
import { ContentType } from '@trellis/api'
import type {
  CreatePushSubscription,
  RequestPagedResult,
  RequestResult,
} from '@trellis/shared'

import { apiClient } from '@/lib/api-client'
import { request, requestPaged } from '@/lib/request'

export interface UpdateNotificationPreferencesPayload {
  preferences: Array<{
    type: string
    role: string
    enabled: boolean
  }>
}

export interface ActionableResponse {
  count: number
  items: NotificationResponseDto[]
}

export interface NotificationsSummaryResponse {
  notifications: {
    items: NotificationResponseDto[]
    nextCursor: string | null
    hasNextPage: boolean
  }
  unreadCount: number
  actionable: ActionableResponse
}

export const listNotifications = (
  params: { cursor?: string; limit?: number } = {},
): Promise<RequestPagedResult<NotificationResponseDto>> =>
  requestPaged<NotificationResponseDto>(() =>
    apiClient.api.request({
      path: '/api/notifications',
      method: 'GET',
      query: params,
      format: 'json',
    }),
  )

export const getUnreadCount = (): Promise<RequestResult<UnreadCountResponseDto>> =>
  request<UnreadCountResponseDto>(() =>
    apiClient.api.request({
      path: '/api/notifications/unread-count',
      method: 'GET',
      format: 'json',
    }),
  )

export const markNotificationRead = (
  notificationId: string,
): Promise<RequestResult<void>> =>
  request<void>(() =>
    apiClient.api.request({
      path: `/api/notifications/${notificationId}/read`,
      method: 'PATCH',
    }),
  )

export const markAllNotificationsRead = (): Promise<RequestResult<void>> =>
  request<void>(() =>
    apiClient.api.request({
      path: '/api/notifications/mark-all-read',
      method: 'POST',
    }),
  )

export const listNotificationPreferences = (): Promise<
  RequestResult<NotificationPreferenceResponseDto[]>
> =>
  request<NotificationPreferenceResponseDto[]>(() =>
    apiClient.api.request({
      path: '/api/notification-preferences',
      method: 'GET',
      format: 'json',
    }),
  )

export const updateNotificationPreferences = (
  payload: UpdateNotificationPreferencesPayload,
): Promise<RequestResult<void>> =>
  request<void>(() =>
    apiClient.api.request({
      path: '/api/notification-preferences',
      method: 'PUT',
      body: payload,
      type: ContentType.Json,
      format: 'json',
    }),
  )

export const snoozeNotification = (
  notificationId: string,
  until: string,
): Promise<RequestResult<void>> =>
  request<void>(() =>
    apiClient.api.request({
      path: `/api/notifications/${notificationId}/snooze`,
      method: 'POST',
      body: { until },
      type: ContentType.Json,
    }),
  )

export const dismissAllNotifications = (): Promise<RequestResult<void>> =>
  request<void>(() =>
    apiClient.api.request({
      path: '/api/notifications/dismiss-all',
      method: 'POST',
    }),
  )

export const getActionableNotifications = (): Promise<
  RequestResult<ActionableResponse>
> =>
  request<ActionableResponse>(() =>
    apiClient.api.request({
      path: '/api/notifications/actionable',
      method: 'GET',
      format: 'json',
    }),
  )

export const getNotificationsSummary = (
  params: { cursor?: string; limit?: number } = {},
): Promise<RequestResult<NotificationsSummaryResponse>> =>
  request<NotificationsSummaryResponse>(() =>
    apiClient.api.request({
      path: '/api/notifications/summary',
      method: 'GET',
      query: params,
      format: 'json',
    }),
  )

export const createPushSubscription = (
  payload: CreatePushSubscription,
): Promise<RequestResult<PushSubscriptionResponseDto>> =>
  request<PushSubscriptionResponseDto>(() =>
    apiClient.api.request({
      path: '/api/notifications/push-subscriptions',
      method: 'POST',
      body: payload,
      type: ContentType.Json,
      format: 'json',
    }),
  )

export const deletePushSubscription = (
  subscriptionId: string,
): Promise<RequestResult<void>> =>
  request<void>(() =>
    apiClient.api.request({
      path: `/api/notifications/push-subscriptions/${subscriptionId}`,
      method: 'DELETE',
    }),
  )
